import { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Logger from './logger';

const DEBUG = (localStorage.getItem('debug')?.indexOf('axiosCache') ?? -1) !== -1;
const logger = Logger('axios_cache', DEBUG ? 'debug' : 'error');
const SLASHES_REGEX = /^\/|\/$/g;

const regexOrStringMatch = (matchPattern: string | RegExp, configUrl: string): boolean => {
  if (matchPattern instanceof RegExp) {
    matchPattern.lastIndex = 0; // Reset the regex to ensure consistent matching
    return matchPattern.test(configUrl);
  }

  return configUrl.includes(matchPattern);
};

// compute hash of axios request config
const hash = async (v: any): Promise<string> => {
  if (v === null || v === undefined) {
    return 'null';
  }

  let { baseURL, url, method } = v;
  const { params, data } = v;
  let auth = null;

  if (v.headers) {
    if (v.headers['Authorization']) {
      auth = v.headers['Authorization'];
    } else if (v.headers['authorization']) {
      auth = v.headers['authorization'];
    }
  }

  // Remove trailing slashes to avoid generating different keys for the "same" final url.
  if (baseURL !== undefined) {
    baseURL = baseURL.replace(SLASHES_REGEX, '');
  } else {
    // just to have a consistent hash
    baseURL = '';
  }

  if (url !== undefined) {
    url = url.replace(SLASHES_REGEX, '');
  } else {
    // just to have a consistent hash
    url = '';
  }

  if (method !== undefined) {
    method = method.toLowerCase();
  } else {
    // just to have a consistent hash
    method = 'get';
  }

  const no = {
    url: baseURL + (baseURL && url ? '/' : '') + url,
    params: params,
    method: method,
    data: data,
    auth: auth,
  };

  // this can be done better - see 'object-code'

  // Convert to string and hash
  const str = JSON.stringify(no);

  // sha256 hash
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));

  // Convert to hex
  const hashArray = Array.from(new Uint8Array(digest));
  const strDigest = hashArray.map((b) => ('00' + b.toString(16)).slice(-2)).join('');

  logger.debug('hash', str, strDigest);

  return strDigest;
};

type CacheResponse = {
  // when the cache expires
  expiresAt: number;
  // successful response
  resolve?: (value: AxiosResponse) => void;
  // failed response
  reject?: (reason?: any) => void;
  // for concurrent requests
  complete?: Promise<AxiosResponse>;
};

type CacheRequestConfig = InternalAxiosRequestConfig & {
  // unique cache key for the request
  cacheKey: string;
};

const responseCache: Map<string, CacheResponse> = new Map();

// cleanup for expired responses
setInterval(() => {
  const now = Date.now();
  responseCache.forEach((value, key) => {
    if (value.expiresAt !== 0 && value.expiresAt < now) {
      logger.debug(`cache ${key} expired`);
      responseCache.delete(key);
    }
  });
}, 1000 * 10); // 10 seconds

type UrlTest = string | RegExp;
type RequestSpec = UrlTest | UrlTest[];

type CacheOptions = {
  allowUrls?: RequestSpec[];
  ignoreUrls?: RequestSpec[];
  allowMethods?: string[];
  ignoreMethods?: string[];
  ttl?: number;
  globalStats?: boolean;
};

let configAxiosCacheTTL = window.config?.axiosCacheTTL ? parseInt(window.config?.axiosCacheTTL) : 1000 * 5; // 5 minutes
if (isNaN(configAxiosCacheTTL) || configAxiosCacheTTL < 0) {
  configAxiosCacheTTL = 1000 * 5; // 5 minutes
}

const defaultCacheOptions: CacheOptions = {
  ttl: configAxiosCacheTTL,
  globalStats: true,
};

class CacheStats {
  private misses: number;
  private hits: number;

  constructor() {
    this.misses = 0;
    this.hits = 0;
  }

  public miss() {
    this.misses++;
  }

  public hit() {
    this.hits++;
  }

  public stats() {
    let ration = 0;
    if (this.hits + this.misses > 0) {
      ration = this.hits / (this.hits + this.misses);
    }
    return `axios cache hits: ${this.hits}, misses: ${this.misses}, ratio: ${ration}`;
  }

  public enableStatsInterval(interval?: number) {
    if (!interval) {
      interval = 1000 * 60;
    }
    setInterval(() => {
      logger.debug(this.stats());
    }, interval);
  }
}

export const globalStats = new CacheStats();

if (DEBUG || import.meta.env.DEV) {
  globalStats.enableStatsInterval(10 * 1000);
}

export const axiosCache = (axios: AxiosInstance, cacheOpts?: CacheOptions): CacheStats => {
  cacheOpts = { ...defaultCacheOptions, ...cacheOpts };

  let newCache: CacheStats;

  if (cacheOpts.globalStats) {
    newCache = globalStats;
  } else {
    newCache = new CacheStats();
  }

  axios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // check allow/ignore urls
      const url = config.baseURL ? config.baseURL + config.url : config.url;
      const method = config.method?.toLocaleLowerCase();

      if (cacheOpts!.allowUrls) {
        let allow = false;

        for (const u of cacheOpts!.allowUrls) {
          if (typeof u === 'string' || u instanceof RegExp) {
            allow = regexOrStringMatch(u, url!);
          } else if (Array.isArray(u)) {
            allow = regexOrStringMatch(u[0], method!) && regexOrStringMatch(u[1], url!);
          }

          if (allow) {
            break;
          }
        }

        if (!allow) {
          logger.debug(`url ${url} not allowed`);
          return config;
        }
      }

      if (cacheOpts!.ignoreUrls) {
        let ignore = false;

        for (const u of cacheOpts!.ignoreUrls) {
          if (typeof u === 'string' || u instanceof RegExp) {
            if (regexOrStringMatch(u, url!)) {
              ignore = true;
            }
          } else if (Array.isArray(u)) {
            if (regexOrStringMatch(u[0], config.method!) && regexOrStringMatch(u[1], url!)) {
              ignore = true;
            }
          }

          if (ignore) {
            break;
          }
        }

        if (ignore) {
          logger.debug(`url ${url} ignored`);
          return config;
        }
      }

      // check allow/ignore methods
      if (cacheOpts!.allowMethods) {
        let allow = false;

        for (const m of cacheOpts!.allowMethods) {
          if (m.toLocaleLowerCase() === method) {
            allow = true;
            break;
          }
        }

        logger.debug(`method ${method} allowed: ${allow}`);

        if (!allow) {
          return config;
        }
      }

      if (cacheOpts!.ignoreMethods) {
        let ignore = false;

        for (const m of cacheOpts!.ignoreMethods) {
          if (m.toLocaleLowerCase() === method) {
            ignore = true;
            break;
          }
        }

        logger.debug(`method ${method} ignored: ${ignore}`);

        if (ignore) {
          return config;
        }
      }

      // cacheable request
      const cacheConfig = config as CacheRequestConfig;

      // compute cache key
      cacheConfig.cacheKey = await hash(config);

      // get cached response
      let cachedResponse = responseCache.get(cacheConfig.cacheKey);

      // check expiration; expiresAt === 0 means response has not yet been received on the first request
      if (cachedResponse && cachedResponse.expiresAt !== 0 && cachedResponse.expiresAt < Date.now()) {
        logger.warn(`cache ${cacheConfig.cacheKey} expired ${cachedResponse.expiresAt}`);

        responseCache.delete(cacheConfig.cacheKey);
        cachedResponse = undefined;
      }

      // if no cached response, create a new one
      if (!cachedResponse) {
        newCache.miss();

        cachedResponse = {
          expiresAt: 0,
        };

        cachedResponse.complete = new Promise((resolve, reject) => {
          cachedResponse!.resolve = resolve;
          cachedResponse!.reject = reject;
        });

        responseCache.set(cacheConfig.cacheKey, cachedResponse);

        cachedResponse.complete!.catch(() => undefined);

        logger.debug(`cache ${cacheConfig.cacheKey} created`);

        return cacheConfig;
      }

      newCache.hit();

      // request already in progress - wait for it to finish
      const ar = await cachedResponse.complete!;

      // check for valid response
      if (!ar) {
        return config;
      }

      // response already received and transformed - no need to do it twice
      cacheConfig.transformResponse = undefined;

      // set the response to the cached one
      cacheConfig.adapter = function cachedAdapter(): Promise<AxiosResponse> {
        return Promise.resolve({
          config,
          data: ar.data,
          headers: ar.headers,
          status: ar.status,
          statusText: ar.statusText,
          cached: true,
        });
      };

      return cacheConfig;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      const cacheConfig = response.config as CacheRequestConfig;

      // check for ignored urls/methods
      if (!(cacheConfig && cacheConfig.cacheKey)) {
        return response;
      }

      const cachedResponse = responseCache.get(cacheConfig.cacheKey);

      // should never happen
      if (!cachedResponse) {
        console.error('No cache response found for key', cacheConfig.cacheKey);
        return response;
      }

      // if this is the first request, set the expiration time
      if (cachedResponse.expiresAt === 0) {
        cachedResponse.expiresAt = Date.now() + cacheOpts!.ttl!;

        logger.debug(`resolved ${cacheConfig.cacheKey} to cache, expires at ${cachedResponse.expiresAt}`);

        // trigger resolve for all waiting requests
        cachedResponse.resolve!(response);
      }

      return response;
    },
    (error) => {
      const cacheConfig = error.config as CacheRequestConfig;

      // check for ignored urls/methods
      if (!(cacheConfig && cacheConfig.cacheKey)) {
        return Promise.reject(error);
      }

      const cachedResponse = responseCache.get(cacheConfig.cacheKey);

      // if no cached response, just reject the error
      if (!cachedResponse) {
        return Promise.reject(error);
      }

      cachedResponse.reject!(error);
      responseCache.delete(cacheConfig.cacheKey);

      return Promise.reject(error);
    },
  );

  return newCache;
};

export const clearCache = (): void => {
  responseCache.clear();
  logger.debug('cache cleared');
};
